import React, { useEffect, useState } from "react";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Textarea from "components/ui/Textarea";
import { ModalHeader, ModalShell } from "components/ui/ModalFrame";
import { DEFAULT_CRM_CATEGORY_ID } from "constants/crm";

const emptyForm = (categoryId) => ({
  id: undefined,
  category_id: categoryId || DEFAULT_CRM_CATEGORY_ID,
  title: "",
  body: "",
  listing_label: "",
  link_url: "",
  is_favorite: false,
});

const TemplateFormModal = ({
  open,
  template,
  categories = [],
  saving,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (template) {
      setForm({
        id: template.id,
        category_id: template.category_id || DEFAULT_CRM_CATEGORY_ID,
        title: template.title || "",
        body: template.body || "",
        listing_label: template.listing_label || "",
        link_url: template.link_url || "",
        is_favorite: Boolean(template.is_favorite),
      });
    } else {
      setForm(emptyForm(categories[0]?.id));
    }
    setError(null);
  }, [open, template, categories]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.title.trim() || !form.body.trim()) {
      setError("Título e texto são obrigatórios.");
      return;
    }
    try {
      await onSave({
        id: form.id,
        category_id: form.category_id,
        title: form.title.trim(),
        body: form.body.trim(),
        listing_label: form.listing_label.trim(),
        link_url: form.link_url.trim(),
        is_favorite: form.is_favorite,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar resposta.");
    }
  };

  return (
    <ModalShell panelClassName="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-elevation-xl border border-border flex flex-col">
      <ModalHeader
        title={form.id ? "Editar resposta" : "Nova resposta"}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <Input
            label="Título"
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Ex.: Saudação inicial"
          />

          <Select
            label="Categoria"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            value={form.category_id}
            onChange={(v) => setForm((f) => ({ ...f, category_id: v }))}
          />

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Texto <span className="text-destructive">*</span>
            </label>
            <Textarea
              required
              rows={8}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Olá {{nome}}, tudo bem? Sobre o {{empreendimento}}…"
              className="min-h-[180px]"
            />
            <p className="text-xs text-muted-foreground">
              Variáveis: {"{{nome}}"} e {"{{empreendimento}}"}
            </p>
          </div>

          <Input
            label="Empreendimento padrão (opcional)"
            value={form.listing_label}
            onChange={(e) =>
              setForm((f) => ({ ...f, listing_label: e.target.value }))
            }
          />
          <Input
            label="Link (opcional)"
            value={form.link_url}
            onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
            placeholder="https://"
          />

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-input"
              checked={form.is_favorite}
              onChange={(e) =>
                setForm((f) => ({ ...f, is_favorite: e.target.checked }))
              }
            />
            Favorita
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Salvando…" : "Salvar resposta"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};

export default TemplateFormModal;
